import React from 'react';
import { graphql, Script, useStaticQuery } from 'gatsby';
import LazyLoad from 'react-lazyload';
import { GlobalStyles } from '@utils/GlobalStyles';
import styles from '@utils/styles';
import Footer from '@components/footer/Footer';
import getHeaderMessage from '@repo/utils/getHeaderMessage';
import getThemeSettings from '@repo/utils/getThemeSettings';
import { InitInsights } from '@repo/utils/algoliaAnalytics';
import SkipToContent from '@repo/ui-components/skip-to-content';
import AccessibilityStatementLink from '@repo/ui-components/AccessibilityStatementLink';
import Cart from '@repo/ui-components/cart/Cart';
import Head from '@repo/ui-components/Head';
import { Header } from '@repo/ui-components/header/Header';
import NotificationBanner from '@repo/ui-components/notification-banner/NotificationBanner';
import { MavenoidChatButton } from '@repo/ui-components/openChatWidget';
import Toast from '@repo/ui-components/cart/Toast';
import getInternalLink from '@repo/utils/getInternalLink';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import { setTrackingConsentCookie } from '@repo/utils/setTrackingConsentCookie';
import shopifyThemeSettings from '../../shopifyThemeSettings.json';
import 'react-image-gallery/styles/css/image-gallery.css';
import '../../styles/globals.css';

const isBrowser = () => typeof window !== 'undefined';

declare global {
  interface Window {
    setTrackingConsentCookie?: typeof setTrackingConsentCookie;
  }
}

function formatMenuItem(item) {
  const formattedMenuItem = item;

  // Add menuType mapping because Shopify doesn't allow for custom fields
  if (formattedMenuItem.title === 'Shop') {
    formattedMenuItem.menuType = 'megaMenu';
  }

  if (formattedMenuItem.title === 'Go Together') {
    formattedMenuItem.menuType = 'dropdown';
  }

  if (formattedMenuItem.title === 'About Kurgo') {
    formattedMenuItem.menuType = 'static';
  }

  if (formattedMenuItem.url) {
    formattedMenuItem.link = {};

    // Add "Shop All" property to collection items
    if (formattedMenuItem.url.includes('/collections')) {
      formattedMenuItem.shopAll = true;
    }

    // Remove domain from url and Shopify page prefixes
    formattedMenuItem.link.href = formattedMenuItem.url
      .replace(/^.*\/\/[^/]+/, '')
      .replace('/pages', '')
      .replace('/collections', '');
    delete formattedMenuItem.url;
  }

  // Formatted nested items
  if (formattedMenuItem.items?.length > 0) {
    formattedMenuItem.items = formattedMenuItem.items.map(menuItem =>
      formatMenuItem(menuItem)
    );
  } else {
    delete formattedMenuItem.items;
  }

  return formattedMenuItem;
}

interface LayoutProps {
  children: React.ReactNode;
  headTagData?: {
    title?: string;
    description?: string;
    keywords?: string;
    meta?: any[];
  };
  scriptsData?: any[];
  omitAnalytics?: boolean;
}

const Layout = ({
  children,
  headTagData = {},
  scriptsData = [],
  omitAnalytics = false,
}: LayoutProps) => {
  const flags = useFlags();
  let headerSettings = null;
  let headerMessage = null;

  // client side redirect for banned domains
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;

    const bannedHostnames = ['bohramatch.com', 'thepetiteflagshop.com'];

    if (bannedHostnames.includes(hostname)) {
      window.location.href = 'https://kurgo.com';
    }
  }

  // build menu from shopify data
  const shopifyMenuData = useStaticQuery(graphql`
    query HeaderQuery {
      shopifyMainMenu {
        items {
          title
          url
          items {
            title
            url
            items {
              title
              url
            }
          }
        }
      }
    }
  `);

  if (!omitAnalytics) InitInsights();

  try {
    headerSettings = getThemeSettings(
      shopifyThemeSettings,
      'header-from-static-site'
    );

    headerMessage = getHeaderMessage(headerSettings);
  } catch (e) {
    console.error('Problem while parsing theme settings.', e);
    headerMessage = null;
  }

  if (isBrowser()) {
    // attach function so available to optanon wrapper
    window.setTrackingConsentCookie = setTrackingConsentCookie;
  }

  return (
    <>
      <GlobalStyles />
      <Head
        title={headTagData.title}
        description={headTagData.description}
        keywords={headTagData.keywords}
        meta={headTagData.meta}
        linkTags={[
          <link
            key="opensearch"
            rel="search"
            href="/opensearch.xml"
            title="Search kurgo.com"
            type="application/opensearchdescription+xml"
          />,
          <link
            key="bv-preconnect"
            rel="preconnect"
            href="//display.ugc.bazaarvoice.com"
          />,
        ]}
        scriptTags={scriptsData}
      >
        <script key="vice-code" type="text/javascript">
          {`vice= {
                config: {
                  viceAccountId: 'petsafe',
                  viceSiteId: 'kurgo',
                  viceSectionId: ''
                }
               }
              `}
        </script>
        <script
          key="vice-prod.sdiapi.com"
          src="https://vice-prod.sdiapi.com/vice_loader/petsafe/kurgo"
        />
        <script
          key="convert"
          src="https://cdn-3.convertexperiments.com/js/10007100-10006551.js"
        />
      </Head>
      <SkipToContent />
      <AccessibilityStatementLink />
      {headerMessage && (
        <NotificationBanner
          messageText={headerMessage.message}
          messageLink={headerMessage.link}
          ChatComponent={MavenoidChatButton}
          getInternalLink={getInternalLink}
          hasDealerSearch={false}
        />
      )}
      <Header
        menuStructure={shopifyMenuData.shopifyMainMenu.items.map(item =>
          formatMenuItem(item)
        )}
        searchIndexes={['products', 'blogs']}
        flags={flags}
        brandName="Kurgo"
      />
      <main id="content">{children}</main>
      <LazyLoad offset={200}>
        <Footer />
      </LazyLoad>
      <Cart
        headerTextColor="white"
        headerBackgroundColor={styles.colors.gray[800]}
        textColor={styles.colors.gray[700]}
        highlightColor={styles.colors.orange[500]}
      />
      <Toast
        headerBackgroundColor={styles.colors.blue[500]}
        headerTextColor="white"
        textColor={styles.colors.gray[700]}
      />

      <Script
        id="gtag-consent-defaults"
        dangerouslySetInnerHTML={{
          __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            functionality_storage: 'denied',
            personalization_storage: 'denied',
            security_storage: 'granted',
            wait_for_update: 200
          });
          gtag('set', 'ads_data_redaction', true);
        `,
        }}
      />

      <Script
        id="optanon-wrapper"
        dangerouslySetInnerHTML={{
          __html: `
          function OptanonWrapper() {
            var analyticsConsent = window.OptanonActiveGroups?.includes('C0002');
            var adsConsent = window.OptanonActiveGroups?.includes('C0004');
            var functionalConsent = window.OptanonActiveGroups?.includes('C0003');
            window.gtag && window.gtag('consent', 'update', {
              analytics_storage: analyticsConsent ? 'granted' : 'denied',
              ad_storage: adsConsent ? 'granted' : 'denied',
              ad_user_data: adsConsent ? 'granted' : 'denied',
              ad_personalization: adsConsent ? 'granted' : 'denied',
              functionality_storage: functionalConsent ? 'granted' : 'denied',
              personalization_storage: analyticsConsent ? 'granted' : 'denied'
            });

            window.setTrackingConsentCookie({
              region: "US",
              domain: ".kurgo.com"
            }); 


            window.dataLayer = window.dataLayer || [];
            window.consentReady = true;
            window.dataLayer.push({
              event: "consent_ready"
            });
          }
        `,
        }}
      />

      <Script
        key="otSDKStub"
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        data-domain-script={process.env.GATSBY_COOKIEPRO_SITE_ID}
      />
      <Script
        key="mavenoid"
        src="https://widget-hosts.mavenoid.com/custom-embedding-scripts/radio-systems-corp.js"
        onLoad={() => {
          window.mavenoid = window?.mavenoid || [];

          // hide mavenoid chat ui behind cookiepro banner
          const observer = new MutationObserver((_, mutationInstance) => {
            const mavenoidComponent =
              document.querySelector('mavenoid-assistant');
            if (mavenoidComponent instanceof HTMLElement) {
              mavenoidComponent.style.zIndex = '5';
              mutationInstance.disconnect();
            }
          });

          observer.observe(document, {
            childList: true,
            subtree: true,
          });
        }}
      />
      <Script
        key="autopilot"
        src="https://cdn.bc0a.com/autopilot/f00000000251950/autopilot_sdk.js"
        defer
      />
      <Script
        key="klaviyo"
        src="https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=gT3Eag"
        async
      />
      <Script
        key="pepperjam"
        src="https://container.pepperjam.com/3142084760.js"
      />
    </>
  );
};

export default Layout;
